import type { EmployeeType, TransactionStatus } from '@/lib/heyapi/types.gen';
import {
  AccountsReceivableCoverage,
  CommercialCertification,
  ConsentType,
  CoverageBasis,
  EarthquakeBuildingClassification,
  EarthquakeCoverage,
  EntityCoverage,
  EntitySharedLimitsType,
  EntityType,
  EquipmentBreakdownDeductible,
  FireWaterLimit,
  FirstNamedInsuredType,
  FloodCoverage,
  HoldHarmlessClause,
  InformedConsentType,
  InpcfParticipation,
  LeaveOfAbsence,
  LossAssessment,
  MissionOfMercyEventType,
  MoonlightingReason,
  NamedStormDeductible,
  NewDentistType,
  OffPremCoverage,
  OrdinanceLimit,
  PlSubTypeEnum,
  PolicyPaymentPlanEnum,
  PolicyTypeEnum,
  PracticeIncomeWaitingPeriod,
  PracticeProfile,
  PracticeSpecialty,
  PremisesAndEquipmentExplanation,
  QuoteTypeEnum,
  RendersService,
  RoofType,
  ScheduleFile,
  SewerBackupCoverage,
  SmokeDetectors,
  SourceSystemEnum,
  StandardOrHigher,
  StopGapLimit,
  Training,
  UnusualRiskExplanation,
  UsStateEnum,
  WeeklyPracticeHours,
  WhoAdministersAnesthesia,
  WindHailCoverage,
  WritingCompanyEnum,
  YearsNoClaims,
  YearsPracticing,
  YesNoEnum,
  YesNoNaEnum,
} from '@/lib/heyapi/types.gen';

import type { Selectable } from './components/Select';

// There's no type and const for ProfessionalDesignation, keeping consistent

export const ProfessionalDesignationTypeEnum = {
  BDS: 'BDS',
  DDS: 'DDS',
  DMD: 'DMD',
  MD: 'MD',
  MS: 'MD',
  MSD: 'MSD',
} as const;

export type ProfessionalDesignationTypeEnum =
  | 'BDS'
  | 'DDS'
  | 'DMD'
  | 'MD'
  | 'MS'
  | 'MSD';

export const ProfessionalDesignationToLabel: Record<
  ProfessionalDesignationTypeEnum,
  string
> = {
  BDS: 'BDS',
  DDS: 'DDS',
  DMD: 'DMD',
  MD: 'MD',
  MS: 'MS',
  MSD: 'MSD',
};

export const EntityCoverageToLabel: Record<EntityCoverage, string> = {
  separate: 'Separate Limits',
  shared: 'Shared Limits',
  none: 'No Professional Liability Coverage Desired',
  share_first_name:
    "All insureds shared in First Named Insured's Limit of Liability",
};

export const SelectableEntityCoverage: Array<Selectable<EntityCoverage>> =
  Object.values(EntityCoverage).map((v) => ({
    value: v,
    label: EntityCoverageToLabel[v],
  }));

export const SelectableProfessionalDesignation: Array<
  Selectable<ProfessionalDesignationTypeEnum>
> = Object.values(ProfessionalDesignationTypeEnum).map((v) => {
  return {
    value: v,
    label: ProfessionalDesignationToLabel[v],
  };
});

export const MercyEventTypeLabel: Record<MissionOfMercyEventType, string> = {
  all: 'All Health Care Providers',
  assistants_only: 'Hygienists & Dental Assistants Only',
};

export const SelectableMissionOfMercyEvent: Array<
  Selectable<MissionOfMercyEventType>
> = Object.values(MissionOfMercyEventType).map((v) => {
  return {
    value: v,
    label: MercyEventTypeLabel[v],
  };
});

// firstNameInsuredType
export const FirstNamedInsuredTypeToLabel: Record<
  FirstNamedInsuredType,
  string
> = {
  multi_individual: 'Multi Individual',
  corporation: 'Corporation',
  individual: 'Individual',
  llc: 'LLC',
  llp: 'LLP',
  other: 'Other',
  partnership: 'Partnership',
  pc_pa: 'PC/PA',
};

export const SelectableFirstNamedInsuredType: Array<
  Selectable<FirstNamedInsuredType>
> = Object.values(FirstNamedInsuredType).map((v) => {
  return {
    value: v as FirstNamedInsuredType,
    label: FirstNamedInsuredTypeToLabel[v as FirstNamedInsuredType],
  };
});

// writingCompany
export const WritingCompanyToLabel: Record<WritingCompanyEnum, string> = {
  asic: 'Aspen Specialty Insurance Company (ASIC)',
  aaic: 'Aspen American Insurance Company (AAIC)',
};

export const SelectableWritingCompany: Array<Selectable<WritingCompanyEnum>> =
  Object.values(WritingCompanyEnum).map((v) => {
    return {
      value: v as WritingCompanyEnum,
      label: WritingCompanyToLabel[v],
    };
  });

// PolicyType
export const PolicyTypeToLabel: Record<PolicyTypeEnum, string> = {
  cancel_rewrite: 'Cancel/Rewrite',
  new_business: 'New Business',
  roll_over: 'Roll-Over',
  renewal: 'Renewal',
};

export const SelectablePolicyType: Array<Selectable<PolicyTypeEnum>> =
  Object.values(PolicyTypeEnum).map((v) => {
    return {
      value: v,
      label: PolicyTypeToLabel[v],
    };
  });

// quoteType
export const QuoteTypeToLabel: Record<QuoteTypeEnum, string> = {
  bindable_quote: 'Bindable Quote',
  preliminary_indication: 'Preliminary Indication',
  policy: 'Policy',
};

// User cannot select the policy type
export const SelectableQuoteType: Array<Selectable<QuoteTypeEnum>> =
  Object.values([
    QuoteTypeEnum.PRELIMINARY_INDICATION,
    QuoteTypeEnum.BINDABLE_QUOTE,
  ]).map((v) => {
    return {
      value: v,
      label: QuoteTypeToLabel[v],
    };
  });

export const OnlyPolicyQuoteType: Array<Selectable<QuoteTypeEnum>> =
  Object.values([QuoteTypeEnum.POLICY]).map((v) => {
    return {
      value: v,
      label: QuoteTypeToLabel[v],
    };
  });

// minimumEarnedPremium
export type MinimumEarnedPremium = '0%' | '25%' | '50%' | '100%';
export const premiums: Array<MinimumEarnedPremium> = [
  '0%',
  '25%',
  '50%',
  '100%',
];

export const SelectableMinimumEarnedPremium: Array<
  Selectable<MinimumEarnedPremium>
> = premiums.map((v) => {
  return {
    value: v,
    label: v.toString(),
  };
});

// paymentPlan
export const PaymentPlanLabels: Record<PolicyPaymentPlanEnum, string> = {
  full: 'Full Pay',
  quarterly: 'Quarterly (33%, 22.333%, 22.333%, 22.333%)',
};

export const SelectablePaymentPlan: Array<Selectable<PolicyPaymentPlanEnum>> =
  Object.values(PolicyPaymentPlanEnum).map((v) => {
    return {
      value: v,
      label: PaymentPlanLabels[v],
    };
  });

// Transaction statuses (not field)
export const TransactionStatusToLabel: Record<TransactionStatus, string> = {
  in_progress: 'In Progress',
  active: 'Active',
  expired: 'Expired',
  declined: 'Declined',
  not_taken: 'Not taken',
  discarded: 'Discarded',
  pending_reversion: 'Pending Reversion',
  reverted: 'Reverted',
  cancelled: 'Cancelled',
};

// coverageBasis
export const CoverageBasisToLabel: Record<CoverageBasis, string> = {
  occurrence: 'Occurrence',
  claims: 'Claims Made',
};

export const SelectableCoverageBasis: Array<Selectable<CoverageBasis>> =
  Object.values(CoverageBasis).map((v) => {
    return {
      value: v,
      label: CoverageBasisToLabel[v],
    };
  });

// consentType
export const ConsentTypeLabels: Record<ConsentType, string> = {
  na: 'N/A',
  first_named_insured: 'First Named Insured',
  insured_dentist: 'Insured Dentist',
};

export const SelectableConsentType: Array<Selectable<ConsentType>> =
  Object.values(ConsentType).map((v) => {
    return {
      value: v,
      label: ConsentTypeLabels[v],
    };
  });

// entityType
export const EntityTypeLabel: Record<EntityType, string> = {
  testing_board: 'Dental Testing Board',
  association: 'Dental Association',
};

export const SelectableEntityType: Array<Selectable<EntityType>> =
  Object.values(EntityType).map((v) => {
    return {
      value: v,
      label: EntityTypeLabel[v],
    };
  });

// employeeType
export const EmployeeTypeLabels: Record<EmployeeType, string> = {
  dentists: 'Dentists',
  oral_surgeons: 'Oral Surgeons',
  dental_anesthesiologists: 'Dental Anesthesiologists',
};

// holdHarmlessClause
export const HoldHarmlessLabels: Record<HoldHarmlessClause, string> = {
  no: 'No',
  yes: 'Yes',
  unsure: 'Unsure',
};

export const SelectableHoldHarmless: Array<Selectable<HoldHarmlessClause>> =
  Object.values(HoldHarmlessClause).map((v) => {
    return { value: v, label: HoldHarmlessLabels[v] };
  });

// plSubTypes
export const PLSubtypeToLabel: Record<PlSubTypeEnum, string> = {
  large_group: 'Large Group (10 Or More Dentists)',
  dental_association: 'Dental Association / Society',
  volunteer_only: 'Volunteer Only',
  faculty_only: 'Faculty Only (Non Intramural)',
  moonlighting: 'Moonlighting',
  new_grad: '2 Year New Grad Policy',
  non_large_dentist: 'Non Large Group (1 to 9 Dentist(s))',
  testing_board: 'Testing Board',
  entity_only: 'Entity Only',
};

const SortedPlSubTypeEnum = {
  NON_LARGE_DENTIST: 'non_large_dentist',
  DENTAL_ASSOCIATION: 'dental_association',
  ENTITY_ONLY: 'entity_only',
  FACULTY_ONLY: 'faculty_only',
  LARGE_GROUP: 'large_group',
  MOONLIGHTING: 'moonlighting',
  NEW_GRAD: 'new_grad',
  TESTING_BOARD: 'testing_board',
  VOLUNTEER_ONLY: 'volunteer_only',
} as const;

export const getSelectablePlSubType = (
  scheduleFile?: ScheduleFile | null
): Array<Selectable<PlSubTypeEnum>> =>
  Object.values(SortedPlSubTypeEnum)
    .filter((v) => {
      // Only show Entity Only when the scheduleFile is Entity Only
      if (v === PlSubTypeEnum.ENTITY_ONLY) {
        return scheduleFile === ScheduleFile.ENTITY_ONLY;
      }
      return true;
    })
    .map((v) => {
      return {
        value: v,
        label: PLSubtypeToLabel[v],
      };
    });

// For backwards compatibility with existing code
export const SelectablePlSubType: Array<Selectable<PlSubTypeEnum>> =
  getSelectablePlSubType();

// export const SelectablePracticeSpecialty: Array<Selectable<PracticeSpecialtyEnum>> =
//   Object.values(PracticeSpecialtyEnum).map((v) => {
//     return {
//       value: v,
//       label: PracticeSpecialtyToLabel[v],
//     };
//   });

// export const WeeklyPracticeHoursToLabel: Record<WeeklyPracticeHours, string> = {

// };

// Use in case you'll need labels
// export const SelectableWeeklyPracticeHours: Array<Selectable<WeeklyPracticeHours>> =
//   Object.entries(WeeklyPracticeHours).map(([k, v]) => {
//     // console.log(k, v)
//     return {
//       value: v,
//       label: WeeklyPracticeHours[k as keyof typeof WeeklyPracticeHours],
//     };
//   });

// SelectablePlaceholder: TODO: Where it's used, it wasn't clear what options should have been used in dropdown
type PlaceholderEnum = 'options' | 'placeholder' | 'report';

export const PlaceholderEnum = {
  OPTIONS: 'options',
  PLACEHOLDER: 'placeholder',
  REPORT: 'report',
} as const;

export const PlaceholderToLabel: Record<PlaceholderEnum, string> = {
  options: 'Options were not defined',
  placeholder: 'Had to use placeholder',
  report: 'Report if found',
};

export const SelectablePlaceholder: Array<Selectable<PlaceholderEnum>> =
  Object.values(PlaceholderEnum).map((v) => {
    return {
      value: v,
      label: PlaceholderToLabel[v],
    };
  });

// YesNoEnum
export const SelectableYesNo: Array<Selectable<YesNoEnum>> = Object.entries(
  YesNoEnum
).map(([k, v]) => {
  return {
    value: v,
    label: YesNoEnum[k as keyof typeof YesNoEnum],
  };
});

export const YesNoToLabel: Record<YesNoEnum, string> = {
  No: 'No',
  Yes: 'Yes',
};

// YesNoNaEnum (Yes/No/N/A) — DC-3448. N/A is a distinct, non-rating answer,
// used only by the dentist "reported situation to current insurer" question.
export const YesNoNAToLabel: Record<YesNoNaEnum, string> = {
  Yes: 'Yes',
  No: 'No',
  'N/A': 'N/A',
};

// Ordered Yes / No / N/A to match the paper application.
export const SelectableYesNoNA: Array<Selectable<YesNoNaEnum>> = [
  YesNoNaEnum.YES,
  YesNoNaEnum.NO,
  YesNoNaEnum.N_A,
].map((v) => ({ value: v, label: YesNoNAToLabel[v] }));

const StopGapLabels: Record<StopGapLimit, string> = {
  '300k': '$300,000',
  '500k': '$500,000',
  '1m': '$1,000,000',
};

export const SelectableStopGapLimit: Array<Selectable<StopGapLimit>> =
  Object.values(StopGapLimit).map((v) => ({
    value: v,
    label: StopGapLabels[v],
  }));

const FireWaterLabels: Record<FireWaterLimit, string> = {
  '500k': '$500,000',
  '1m': '$1,000,000',
  '750k': '$750,000',
};

export const SelectableFireWaterLimit: Array<Selectable<FireWaterLimit>> =
  Object.values(FireWaterLimit).map((v) => ({
    value: v,
    label: FireWaterLabels[v],
  }));

// UsStateEnum

export const UnitedStatesToLabel: Record<UsStateEnum, string> = {
  AK: 'Alaska',
  AL: 'Alabama',
  AR: 'Arkansas',
  // AS: 'American Samoa',
  AZ: 'Arizona',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DC: 'District of Columbia',
  DE: 'Delaware',
  FL: 'Florida',
  // FM: 'Federated States of Micronesia',
  GA: 'Georgia',
  // GU: 'Guam',
  HI: 'Hawaii',
  IA: 'Iowa',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  KS: 'Kansas',
  KY: 'Kentucky',
  LA: 'Louisiana',
  MA: 'Massachusetts',
  MD: 'Maryland',
  ME: 'Maine',
  // MH: 'Marshall Islands',
  MI: 'Michigan',
  MN: 'Minnesota',
  MO: 'Missouri',
  // MP: 'Northern Mariana Islands',
  MS: 'Mississippi',
  MT: 'Montana',
  NC: 'North Carolina',
  ND: 'North Dakota',
  NE: 'Nebraska',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NV: 'Nevada',
  NY: 'New York',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  PR: 'Puerto Rico',
  // PW: 'Palau',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VA: 'Virginia',
  VI: 'Virgin Islands',
  VT: 'Vermont',
  WA: 'Washington',
  WI: 'Wisconsin',
  WV: 'West Virginia',
  WY: 'Wyoming',
};

export const SelectableUnitedStates: Array<Selectable<UsStateEnum>> =
  Object.values(UsStateEnum).map((v) => {
    return {
      value: v,
      label: UnitedStatesToLabel[v],
    };
  });

export const SelectableUnitedStatesNewGrad: Array<Selectable<UsStateEnum>> =
  Object.values(UsStateEnum)
    .map((v) => {
      return {
        value: v,
        label: UnitedStatesToLabel[v],
      };
    })
    // removing VI(Virgin Islands) from primary practice state - new grad form
    .filter((v) => v.value !== UsStateEnum.VI);

// IN PCF Fund Participation?
export const InpcfParticipationToLabel: Record<InpcfParticipation, string> = {
  refer: 'Refer to Agent',
  yes: 'Yes',
  no: 'No',
};

export const SelectableInpcfParticipation: Array<
  Selectable<InpcfParticipation>
> = Object.values(InpcfParticipation).map((v) => {
  return {
    value: v,
    label: InpcfParticipationToLabel[v],
  };
});

// Years In Practice
export const YearsPracticingToLabel: Record<YearsPracticing, string> = {
  y0: '0',
  y1: '1',
  y2: '2',
  y3: '3',
  gte4: '4+',
};

export const SelectableYearsPracticing: Array<Selectable<YearsPracticing>> =
  Object.values(YearsPracticing).map((v) => {
    return {
      value: v,
      label: YearsPracticingToLabel[v],
    };
  });

// New Dentist Type
export const NewDentistTypeToLabel: Record<NewDentistType, string> = {
  foreign: 'Foreign Dental School Graduate',
  new_grad: 'New Grad',
  military: 'Military Dentist',
  public: 'Public Service Sector',
};

export const SelectableNewDentistType: Array<Selectable<NewDentistType>> =
  Object.values(NewDentistType).map((v) => {
    return {
      value: v,
      label: NewDentistTypeToLabel[v],
    };
  });

// Number of Hours per Week in Practice
export const WeeklyPracticeHoursToLabel: Record<WeeklyPracticeHours, string> = {
  fulltime: 'Full-Time (More Than 20 Hours Per Week)',
  parttime: 'Part-Time (20 Hours Or Less)',
  moonlighting: 'Moonlighting (10 Hours Or Less Per Week And For Second Job)',
};

export const SelectableWeeklyPracticeHours: Array<
  Selectable<WeeklyPracticeHours>
> = Object.values(WeeklyPracticeHours).map((v) => {
  return {
    value: v,
    label: WeeklyPracticeHoursToLabel[v],
  };
});

export const WeeklyPracticeHoursWithoutMoonlighting: Array<
  Selectable<WeeklyPracticeHours>
> = [WeeklyPracticeHours.FULLTIME, WeeklyPracticeHours.PARTTIME].map((v) => {
  return {
    value: v,
    label: WeeklyPracticeHoursToLabel[v],
  };
});

// moonlightingReason
export const MoonlightingReasonToLabel: Record<MoonlightingReason, string> = {
  residency: 'Residency',
  military: 'Military',
  second_job: 'Second Job',
  other: 'Other',
};

export const SelectableMoonlightingReason: Array<
  Selectable<MoonlightingReason>
> = Object.values(MoonlightingReason).map((v) => {
  return {
    value: v,
    label: MoonlightingReasonToLabel[v],
  };
});

// Practice Profile
export const PracticeProfileToLabel: Record<PracticeProfile, string> = {
  employee: 'Employee Dentist',
  faculty: 'Full-Time Faculty (No Intramural)',
  contractor: 'Independent Contractor Dentist',
  owner: 'Practice Owner',
  volunteer: 'Volunteer Only',
};

export const SelectablePracticeProfile: Array<Selectable<PracticeProfile>> =
  Object.values(PracticeProfile).map((v) => {
    return {
      value: v,
      label: PracticeProfileToLabel[v],
    };
  });

// yearsWithoutClaim
export const YearsNoClaimsToLabel: Record<YearsNoClaims, string> = {
  y0: '0',
  y1: '1',
  y2: '2',
  y3: '3',
  y4: '4',
  y5: '5',
  y6: '6',
  y7: '7',
  y8: '8',
  y9: '9',
  gte10: '10+',
};

export const SelectableYearsNoClaims: Array<Selectable<YearsNoClaims>> =
  Object.values(YearsNoClaims).map((v) => {
    return {
      value: v,
      label: YearsNoClaimsToLabel[v],
    };
  });

// Leave of Absence
export const LeaveOfAbsenceToLabel: Record<LeaveOfAbsence, string> = {
  initiate_regular: 'Initiate Regular',
  initiate_military: 'Initiate Military',
};

// TODO: BE: rules: When previous transaction "Leave of Absence" INCLUDES ("INITIATE REGULAR")
// "INITIATE REGULAR"
// When previous transaction "Leave of Absence" INCLUDES ("INITIATE MILITARY")
// "INITIATE MILITARY"
// Note: Latest transaction does not have this data, or should it be taken from general fields data? Seems to be equal

export const SelectableLeaveOfAbsence: Array<Selectable<LeaveOfAbsence>> =
  Object.values(LeaveOfAbsence).map((v) => {
    return {
      value: v,
      label: LeaveOfAbsenceToLabel[v],
    };
  });

// informedConsentTypes
export const InformedConsentTypeToLabel: Record<InformedConsentType, string> = {
  written: 'Written',
  oral: 'Oral',
  none: 'None',
};

export const SelectableInformedConsentType: Array<
  Selectable<InformedConsentType>
> = Object.values(InformedConsentType).map((v) => {
  return {
    value: v,
    label: InformedConsentTypeToLabel[v],
  };
});

// trainings
export const TrainingToLabel: Record<Training, string> = {
  ce: 'CE',
  school: 'Dental School',
  post_grad: 'Post Grad',
};

export const SelectableTraining: Array<Selectable<Training>> = Object.values(
  Training
).map((v) => {
  return {
    value: v,
    label: TrainingToLabel[v],
  };
});

// whoAdministersAnesthesia
export const WhoAdministersAnesthesiaToLabel: Record<
  WhoAdministersAnesthesia,
  string
> = {
  another: 'Another Licensed Professional',
  insured: 'Insured Dentist',
};

export const SelectableWhoAdministersAnesthesia: Array<
  Selectable<WhoAdministersAnesthesia>
> = Object.values(WhoAdministersAnesthesia).map((v) => {
  return {
    value: v,
    label: WhoAdministersAnesthesiaToLabel[v],
  };
});

export const PracticeSpecialtyToLabel: Record<PracticeSpecialty, string> = {
  oral_surgery: 'Oral Surgeon',
  anesthesiologist:
    'Dental Anesthesiologist No Deep Sedation or General Anesthesia',
  orthodontics: 'Orthodontics',
  endodontics: 'Endodontics',
  general: 'General Dentistry',
  pediatric: 'Pediatric Dentistry',
  periodontics: 'Periodontics',
  prosthodontics: 'Prosthodontics',
  public: 'Public Health',
  oral_medicine: 'Oral Medicine',
  forensic: 'Forensic Dentistry',
  oral_radiology: 'Oral Radiology',
  orofacial: 'Orofacial Pain',
  oral_pathology: 'Oral Pathology',
  anesthesiologist_deep_sedation:
    'Dental Anesthesiologist Includes Deep Sedation or General Anesthesia',
};

export const SelectablePracticeSpecialty: Array<Selectable<PracticeSpecialty>> =
  Object.values(PracticeSpecialty)
    .map((v) => {
      return {
        value: v,
        label: PracticeSpecialtyToLabel[v],
      };
    })
    .sort();

// practiceIncomeWaitingPeriod
export const PracticeIncomeWaitingPeriodToLabel: Record<
  PracticeIncomeWaitingPeriod,
  string
> = {
  zero: '0 Hours (0 Days)',
  twenty_four: '24 Hours (1 Day)',
  forty_eight: '48 Hours (2 Days)',
  seventy_two: '72 Hours (3 Days)',
  ninety_six: '96 Hours (4 Days)',
  one_twenty: '120 Hours (5 Days)',
  one_fourty_four: '144 Hours (6 Days)',
  one_sixty_eight: '168 Hours (7 Days)',
  one_ninety_six: '192 Hours (8 Days)',
  two_sixteen: '216 Hours (9 Days)',
  two_forty: '240 Hours (10 Days)',
  three_sixty: '360 Hours (15 Days)',
  four_eighty: '480 Hours (20 Days)',
  six_hundred: '600 Hours (25 Days)',
};

export const SelectablePracticeIncomeWaitingPeriod: Array<
  Selectable<PracticeIncomeWaitingPeriod>
> = Object.values(PracticeIncomeWaitingPeriod).map((v) => {
  return {
    value: v,
    label: PracticeIncomeWaitingPeriodToLabel[v],
  };
});

// windhailCoverage
export const WindHailCoverageToLabel: Record<WindHailCoverage, string> = {
  location_fixed_deductible: 'Location Flat Deductible Above',
  one_pct: '1%',
  '2%': '2%',
  five_pct: '5%',
  excluded: 'Wind/Hail Excluded',
};

export const SelectableWindHailCoverage: Array<Selectable<WindHailCoverage>> =
  Object.values(WindHailCoverage).map((v) => {
    return {
      value: v,
      label: WindHailCoverageToLabel[v],
    };
  });

// namedStormDeductible
export const NamedStormDeductibleToLabel: Record<NamedStormDeductible, string> =
  {
    none: 'NONE',
    one_pct: '1%',
    two_pct: '2%',
    '5%': '5%',
  };

export const SelectableNamedStormDeductible: Array<
  Selectable<NamedStormDeductible>
> = Object.values(NamedStormDeductible).map((v) => {
  return {
    value: v,
    label: NamedStormDeductibleToLabel[v],
  };
});

// equipmentBreakdownDeductible
export const EquipmentBreakdownDeductibleToLabel: Record<
  EquipmentBreakdownDeductible,
  string
> = {
  500: '$500',
  1000: '$1,000',
  2500: '$2,500',
  5000: '$5,000',
  10000: '$10,000',
};

export const SelectableEquipmentBreakdownDeductible: Array<
  Selectable<EquipmentBreakdownDeductible>
> = Object.values(EquipmentBreakdownDeductible).map((v) => {
  return {
    value: v,
    label: EquipmentBreakdownDeductibleToLabel[v],
  };
});

// electronicEquipCoverage
export const ElectronicEquipCoverageToLabel: Record<StandardOrHigher, string> =
  {
    standard: 'Standard Limit',
    higher: 'Higher Limit',
  };

export const SelectableElectronicEquipCoverage: Array<
  Selectable<StandardOrHigher>
> = Object.values(StandardOrHigher).map((v) => {
  return {
    value: v,
    label: ElectronicEquipCoverageToLabel[v],
  };
});

// TODO: sewerBackupCoverage StandardOrHigher missing enum options, should be: "No", "Standard Limit", "$50,000", "$100,000", "Higher Limit"
// sewerBackupCoverage
export const SewerBackupCoverageToLabel: Record<SewerBackupCoverage, string> = {
  standard: 'Standard Limit',
  higher: 'Higher Limit',
  '50k': '$50,000',
  '100k': '$100,000',
};

export const SelectableSewerBackupCoverage: Array<
  Selectable<SewerBackupCoverage>
> = Object.values(SewerBackupCoverage).map((v) => {
  return {
    value: v,
    label: SewerBackupCoverageToLabel[v],
  };
});

// pollutionLimitCoverage
export const PollutionLimitCoverageToLabel: Record<StandardOrHigher, string> = {
  standard: 'Standard Limit',
  higher: 'Higher Limit',
};

export const SelectablePollutionLimitCoverage: Array<
  Selectable<StandardOrHigher>
> = Object.values(StandardOrHigher).map((v) => {
  return {
    value: v,
    label: PollutionLimitCoverageToLabel[v],
  };
});

// floodCoverage
export const FloodCoverageToLabel: Record<FloodCoverage, string> = {
  standard: 'Standard Limit',
  higher: 'Higher Limit',
  not_eligible: 'Not Eligible',
};

export const SelectableFloodCoverage: Array<Selectable<FloodCoverage>> =
  Object.values(FloodCoverage).map((v) => {
    return {
      value: v,
      label: FloodCoverageToLabel[v],
    };
  });

// earthquakeCoverage
export const EarthquakeCoverageToLabel: Record<EarthquakeCoverage, string> = {
  standard: 'Standard Limit',
  higher: 'Higher Limit',
  not_eligible: 'Not Eligible',
  none: 'None',
  desired: 'Coverage Desired',
};

export const SelectableEarthquakeCoverage: Array<
  Selectable<EarthquakeCoverage>
> = Object.values(EarthquakeCoverage).map((v) => {
  return {
    value: v,
    label: EarthquakeCoverageToLabel[v],
  };
});

// earthquakeBuildingClass
export const EarthquakeBuildingClassificationToLabel: Record<
  EarthquakeBuildingClassification,
  string
> = {
  A1: 'Class A1 - WOOD FRAME OR FRAME STUCCO BUILDINGS. EXCLUDED ARE ANY BUILDING WITH CONCRETE SUPPORTED FLOORS OR WALLS',
  B1: 'Class B1 - ALL METAL BUILDINGS. WOOD OR CEMENT-ASBESTOS ARE ACCEPTABLE ALTERNATIVES TO METAL ROOFING AND SIDING',
  C1: 'Class C1 - BUILDINGS WITH COMPLETE STEEL FRAME CARRYING ALL LOADS WITH FLOORS AND ROOFS OF ANY MATERIAL',
  D1: 'Class D1 - BUILDINGS HAVING ALL VERTICAL LOADS CARRIED BY STRUCTURAL SYSTEM CONSISTING OF EITHER POURED IN PLACE REINFORCED CONCRETE FRAME, POURED-IN PLACE REFINFORCED CONCRETE WALLS. FLOORS AND ROOF MUST BE POURED IN PLACE REINFORCED CONCRETE',
  D2: 'Class D2 - BUILDINGS HAVING REINFORCED CONCRETE FRAME OR COMBINED REINFORCED CONCRETE AND STRUCTURAL STEEL FRAME. FLOOR AND ROOF OF ANY MATERIAL',
  D3: 'Class D3 - BUILDINGS HAVING PARTIAL OR COMPLETE LOAD-CARRYING SYSTEM OF PRECAST CONCRETE AND/OR REINFORCED CONCRETE LIFT-SLAB FLOORS',
  E1: 'Class E1 - BUILDINGS HAVING LOAD BEARING WALLS OF REINFORCED BRICK MASONRY AND/OR REINFORCED HOLLOW CONCRETE BLOCK',
  E2: 'Class E2 - BUILDINGS HAVING LOAD BEARING WALLS OF UNREINFORCED BRICK, OTHER UNREINFORCED SOLID UNIT MASONRY, HOLLOW TILE OR OTHER HOLLOW UNIT MASONRY CONSTRUCTION. FLOORS AND ROOF OF ANY MATERIAL',
  E3: 'Class E3 - BUILDINGS HAVING ALL LOAD BEARING WALLS OF ADOBE',
};

export const SelectableEarthquakeBuildingClassification: Array<
  Selectable<EarthquakeBuildingClassification>
> = Object.values(EarthquakeBuildingClassification).map((v) => {
  return {
    value: v,
    label: EarthquakeBuildingClassificationToLabel[v],
  };
});

// lossAssessmentCoverage
export const LossAssessmentToLabel: Record<LossAssessment, string> = {
  higher: 'Higher Limit',
  no: 'No',
  one_k: '$1,000',
  five_k: '$5,000',
  ten_k: '$10,000',
  twenty_five_k: '$25,000',
  fifty_k: '$50,000',
  one_hundred_k: '$100,000',
};

export const SelectableLossAssessment: Array<Selectable<LossAssessment>> =
  Object.values(LossAssessment).map((v) => {
    return {
      value: v,
      label: LossAssessmentToLabel[v],
    };
  });

// offPremCoverage
export const OffPremCoverageToLabel: Record<OffPremCoverage, string> = {
  standard: 'Standard Limit',
  higher: 'Higher Limit',
  none: 'None',
};

export const SelectableOffPremCoverage: Array<Selectable<OffPremCoverage>> =
  Object.values(OffPremCoverage).map((v) => {
    return {
      value: v,
      label: OffPremCoverageToLabel[v],
    };
  });

// accountsReceivableCoverage
export const AccountsReceivableCoverageToLabel: Record<
  AccountsReceivableCoverage,
  string
> = {
  standard: 'Standard Limit',
  higher: 'Higher Limit',
  none: 'None',
};

export const SelectableAccountsReceivableCoverage: Array<
  Selectable<AccountsReceivableCoverage>
> = Object.values(AccountsReceivableCoverage).map((v) => {
  return {
    value: v,
    label: AccountsReceivableCoverageToLabel[v],
  };
});

// smokeDetectors
export const SmokeDetectorsToLabel: Record<SmokeDetectors, string> = {
  no: 'No Smoke Detector',
  central: 'Central Smoke Detector',
  local: 'Local Smoke Detector',
};

export const SelectableSmokeDetectors: Array<Selectable<SmokeDetectors>> =
  Object.values(SmokeDetectors).map((v) => {
    return {
      value: v,
      label: SmokeDetectorsToLabel[v],
    };
  });

// roofType
export const RoofTypeToLabel: Record<RoofType, string> = {
  metal: 'Metal',
  other: 'Other than Metal',
};

export const SelectableRoofType: Array<Selectable<RoofType>> = Object.values(
  RoofType
).map((v) => {
  return {
    value: v,
    label: RoofTypeToLabel[v],
  };
});

// fortifiedCommercialCertification
export const FortifiedCommercialCertificationToLabel: Record<
  CommercialCertification,
  string
> = {
  bronze: 'Bronze/Roof Certification',
  silver: 'Silver Certification',
  gold: 'Gold Certification',
  not_applicable: 'Not Applicable',
};

export const SelectableFortifiedCommercialCertification: Array<
  Selectable<CommercialCertification>
> = Object.values(CommercialCertification).map((v) => {
  return {
    value: v,
    label: FortifiedCommercialCertificationToLabel[v],
  };
});

// signsCoverage
export const SignsCoverageToLabel: Record<StandardOrHigher, string> = {
  standard: 'Standard Limit',
  higher: 'Higher Limit',
};

export const SelectableSignsCoverage: Array<Selectable<StandardOrHigher>> =
  Object.values(StandardOrHigher).map((v) => {
    return {
      value: v,
      label: SignsCoverageToLabel[v],
    };
  });

// demolitionIncreasedLimitDesired
export const DemolitionIncreasedLimitDesiredToLabel: Record<
  OrdinanceLimit,
  string
> = {
  standard: 'Standard Limit ($100,000 Combined 1, 2, 3)',
  higher: 'Higher Limit',
};

export const SelectableDemolitionIncreasedLimitDesired: Array<
  Selectable<OrdinanceLimit>
> = Object.values(OrdinanceLimit).map((v) => {
  return {
    value: v,
    label: DemolitionIncreasedLimitDesiredToLabel[v],
  };
});

// employeeDishonestyCoverageChoice
export const EmployeeDishonestyCoverageChoiceToLabel: Record<
  StandardOrHigher,
  string
> = {
  standard: 'Standard Limit',
  higher: 'Higher Limit',
};

export const SelectableEmployeeDishonestyCoverageChoice: Array<
  Selectable<StandardOrHigher>
> = Object.values(StandardOrHigher).map((v) => {
  return {
    value: v,
    label: EmployeeDishonestyCoverageChoiceToLabel[v],
  };
});

// renders service
export const RendersServiceToLabel: Record<RendersService, string> = {
  spa: 'Medi-spa',
  correctional: 'Correctional Facility',
  hospital: 'Hospital / Urgent Care Center',
  school: 'K - 12 School',
  nursing_home: 'Nursing Home Facility',
  mobile: 'Mobile Dentistry',
  none: 'None of These',
};

export const SelectableRendersService: Array<
  Selectable<RendersService> & { exclusive?: boolean }
> = Object.values(RendersService).map((v) => {
  return {
    value: v,
    label: RendersServiceToLabel[v],
    exclusive: v === RendersService.NONE, // Mark "None of These" as exclusive
  };
});

// additionalUnusualRiskCharacteristicsExplanation
export const UnusualRiskExplanationToLabel: Record<
  UnusualRiskExplanation,
  string
> = {
  documented_plan:
    'DOCUMENTED MEDICAL EMERGENCY PLAN IN PLACE, UPDATED AND REHEARSED',
  comprehensive_infection_control:
    'COMPREHENSIVE, DOCUMENTED INFECTION CONTROL PROCEDURES IN EFFECT',
  welldesigned_system:
    'WELL-DESIGNED, FOLLOW-UP SYSTEM IN EFFECT FOR LAB REPORTS, CONSULTS AND APPOINTMENTS',
  trained_practice_manager: 'TRAINED PRACTICE MANAGER ON STAFF',
  continuing_education:
    'DOCUMENTED PROGRAM IN PLACE FOR CONTINUING EDUCATION OF STAFF',
  no_risk_manager: 'NO QUALIFIED RISK MANAGER ON STAFF',
  no_protocols: 'NO DOCUMENTED RISK MANAGEMENT PROTOCOLS',
  no_training:
    'NO COMPREHENSIVE RISK MANAGEMENT TRAINING FOR ALL KEY PERSONNEL',
};

export const SelectableUnusualRiskExplanation: Array<
  Selectable<UnusualRiskExplanation>
> = Object.values(UnusualRiskExplanation)
  .map((v) => {
    return {
      value: v,
      label: UnusualRiskExplanationToLabel[v],
    };
  })
  .sort();

// irpmPremisesAndEquipmentExplanationV2
export const PremisesAndEquipmentExplanationToLabel: Record<
  PremisesAndEquipmentExplanation,
  string
> = {
  credit_monitored_system: 'THIRD PARTY MONITORED SECURITY SYSTEM INSTALLED',
  credit_good_neighbor: 'NEAREST NEIGHBORING OCCUPANCIES ARE OFFICE RISKS',
  credit_easily_accessible:
    'PREMISES IS EASILY ACCESSIBLE TO EMERGENCY RESPONDERS',
  credit_third_party: 'THIRD PARTY CONTRACTED FOR PARKING LOT MAINTENANCE',
  credit_experienced_management: 'EXPERIENCED MANAGEMENT TEAM (5+ YEARS)',

  debit_no_monitored_system:
    'THIRD PARTY MONITORED SECURITY SYSTEM NOT INSTALLED',
  debit_bad_neighbor:
    'NEAREST NEIGHBORING OCCUPANCIES ARE RESTAURANT OR MANUFACTURING',
  debit_vacant: 'BULDING IS MORE THAN 10% UNOCCUPIED/VACANT',
  debit_inexperienced_management:
    'MANAGEMENT TEAM HAS LESS THAN 5 YEARS EXPERIENCE',
};

export const SelectablePremisesAndEquipmentExplanation: Array<
  Selectable<PremisesAndEquipmentExplanation>
> = Object.values(PremisesAndEquipmentExplanation).map((v) => {
  return {
    value: v,
    label: PremisesAndEquipmentExplanationToLabel[v],
  };
});

// scheduleFile
export const ScheduleFileToLabel: Record<ScheduleFile, string> = {
  large_group: 'Yes - Large Group',
  no: 'No',
  entity_only: 'Yes - Entity Only',
};

export const SelectableScheduleFile: Array<Selectable<ScheduleFile>> =
  Object.values(ScheduleFile)
    .map((v) => {
      return {
        value: v,
        label: ScheduleFileToLabel[v],
      };
    })
    .sort((i) => (i.value === 'no' ? 1 : -1));

// Entity Shared Limits Type
export const EntitySharedLimitsTypeToLabel: Record<
  EntitySharedLimitsType,
  string
> = {
  shared_with_member_dentists: 'Shared with Member Dentists',
  shared_with_other_entities: 'Shared with Other Entities',
};

export const SelectableEntitySharedLimitsType: Array<
  Selectable<EntitySharedLimitsType>
> = Object.values(EntitySharedLimitsType).map((v) => {
  return {
    value: v,
    label: EntitySharedLimitsTypeToLabel[v],
  };
});

// SourceSystemEnum
export const SourceSystemEnumToLabel: Record<SourceSystemEnum, string> = {
  mountaineer: 'Mountaineer',
  new_grad_app: 'New Grad App',
};

export const SelectableSourceSystemEnum: Array<Selectable<SourceSystemEnum>> =
  Object.values(SourceSystemEnum).map((v) => {
    return {
      value: v,
      label: SourceSystemEnumToLabel[v],
    };
  });
